import { effect, inject, Injectable } from '@angular/core';
import { Subscription } from 'rxjs';

import { AuthService } from './auth.service';
import { SettingsService } from './settings.service';
import { ThreadEventsService } from './thread-events.service';
import { ToastService } from './toast.service';
import { WalletService } from './wallet.service';
import { ExtraUsageCtaStore } from '../pages/chat-with-collectiv-iq/chat-panel/chat-form/extra-usage-cta/extra-usage-cta.store';

export type ExtraUsageRequestedEvent = {
    type: 'extra_usage_requested';
    member_name: string;
    window: 'daily' | 'monthly';
    /** The workspaces billed to the wallet the request is on. */
    workspace_ids?: number[];
};

export type ExtraUsageChangedEvent = {
    type: 'extra_usage_changed';
    enabled: boolean;
    /** The customer user who made the change; null for an operator. */
    actor_user_id: number | null;
    /** The workspaces billed to the wallet whose setting changed. */
    workspace_ids?: number[];
};

/** Requests arriving this close together share one toast. */
export const EXTRA_USAGE_TOAST_GATHER_MS = 2000;
/** After a toast, later requests wait this long and are summed into one toast. */
export const EXTRA_USAGE_TOAST_COOLDOWN_MS = 60000;

/**
 * Tells an owner or admin that members asked for extra usage past a plan cap (CIQ-2230). The server sends
 * `extra_usage_requested` only to the wallet's owners and admins, and it shows only while one of that wallet's
 * workspaces is the active one, where the requests table lists it. Requests close together share one toast, and
 * at most one toast shows per cooldown, so many requests at once do not flood the screen. An open requests table
 * reloads at most once per gather window. The toast's Review requests button
 * opens the requests table. Eagerly injected in the app root so the toast shows on any route.
 */
@Injectable({ providedIn: 'root' })
export class ExtraUsageRequestNotificationsService {
    private readonly auth = inject(AuthService);
    private readonly threadEvents = inject(ThreadEventsService);
    private readonly toast = inject(ToastService);
    private readonly settings = inject(SettingsService);
    private readonly wallet = inject(WalletService);
    private readonly ctaStore = inject(ExtraUsageCtaStore);

    private sseSub: Subscription | null = null;
    private pending: { name: string; workspaceIds: number[] | undefined }[] = [];
    private flushTimer: ReturnType<typeof setTimeout> | null = null;
    private reloadTimer: ReturnType<typeof setTimeout> | null = null;
    private coolingUntil = 0;

    // noinspection JSUnusedLocalSymbols
    private readonly _onAuth = effect(() => {
        if (this.auth.state().user) {
            this.start();
        } else {
            this.stop();
        }
    });

    start(): void {
        if (this.sseSub) {
            return;
        }
        this.sseSub = this.threadEvents.connect().subscribe(evt => {
            const e = evt as unknown as Partial<ExtraUsageRequestedEvent> & { type?: string };
            if (e.type === 'extra_usage_requested') {
                this.show(e);
            } else if (e.type === 'extra_usage_changed') {
                this.applyChange(e as Partial<ExtraUsageChangedEvent>);
            }
        });
    }

    stop(): void {
        this.sseSub?.unsubscribe();
        this.sseSub = null;
        for (const timer of [this.flushTimer, this.reloadTimer]) {
            if (timer) {
                clearTimeout(timer);
            }
        }
        this.flushTimer = null;
        this.reloadTimer = null;
        this.pending = [];
        this.coolingUntil = 0;
    }

    /** Only an owner or admin of the active workspace, when that workspace is on the request's wallet, can review it. */
    private canReview(workspaceIds: number[] | undefined): boolean {
        const user = this.auth.state().user;
        if (user?.workspace_role !== 'owner' && user?.workspace_role !== 'admin') {
            return false;
        }
        return !workspaceIds || (user.workspace_id != null && workspaceIds.includes(user.workspace_id));
    }

    /** The viewer's own extra usage changed: lift or restore the plan-cap block without a reload. */
    applyChange(event: Partial<ExtraUsageChangedEvent>): void {
        const user = this.auth.state().user;
        if (!user || (event.workspace_ids && (user.workspace_id == null || !event.workspace_ids.includes(user.workspace_id)))) {
            return;
        }
        this.ctaStore.reset();
        this.settings.noteExtraUsageChanged();
        this.wallet.fetchCanSend().subscribe();
        if (event.enabled && event.actor_user_id !== user.user_id) {
            this.toast.success('Extra usage is on for you. You can keep working past the plan limit.');
        }
    }

    show(event: Partial<ExtraUsageRequestedEvent>): void {
        if (!this.canReview(event.workspace_ids)) {
            return;
        }
        this.pending.push({ name: event.member_name?.trim() || '', workspaceIds: event.workspace_ids });
        this.reloadTimer ??= setTimeout(() => {
            this.reloadTimer = null;
            this.settings.noteExtraUsageRequested();
        }, EXTRA_USAGE_TOAST_GATHER_MS);
        if (this.flushTimer) {
            return;
        }
        const wait = Math.max(EXTRA_USAGE_TOAST_GATHER_MS, this.coolingUntil - Date.now());
        this.flushTimer = setTimeout(() => this.flush(), wait);
    }

    private flush(): void {
        this.flushTimer = null;
        const names = this.pending.filter(p => this.canReview(p.workspaceIds)).map(p => p.name);
        this.pending = [];
        if (!names.length) {
            return;
        }
        this.coolingUntil = Date.now() + EXTRA_USAGE_TOAST_COOLDOWN_MS;
        void this.toast.infoWithAction(
            `${this.who(names)} for extra usage.`,
            'Review requests',
            () => this.settings.openExtraUsageRequests(),
            { actionTestId: 'extra-usage-request-toast-review' }
        );
    }

    private who(names: string[]): string {
        const first = names.find(Boolean);
        if (names.length === 1) {
            return `${first || 'A member'} asked`;
        }
        const others = names.length - 1;
        return first ? `${first} and ${others} other${others === 1 ? '' : 's'} asked` : `${names.length} members asked`;
    }
}
